import { defineStore } from 'pinia'
import type { Household, Registration, Representative, Sleeve, SleevesPerReg } from '~/types'
import { buildSidebarCacheEnvelope } from '~/utils/sidebarCache'
import type { SidebarCacheEnvelope } from '~/utils/sidebarCache'

/**
 * TTLs (tune as needed)
 */
const TTL = {
  households: 10 * 60_000, // 10 min
  registrations: 5 * 60_000, // 5 min
  sleeves: 5 * 60_000, // 5 min
  representatives: 10 * 60_000 // 10 min
}

type Role = 'admin' | 'client' | 'user' | string

type FetchOptions = {
  force?: boolean
  background?: boolean // stale-while-revalidate
  role?: Role | null
  accountNumber?: string | null
}

export const useTabSessionStore = defineStore('tab-session', {
  state: () => ({
    // ─────────────────────────────────────────────
    // Persistent IDs (tab-scoped)
    // ─────────────────────────────────────────────
    householdId: null as number | null,
    registrationId: null as string | null,
    sleeveId: null as string | null,
    representativeId: null as number | null,

    // Backend data version (IQueryCache generation) the cached data was
    // fetched at. Surfaced on /api/me/last-import; bumps on every import
    // success + admin cache-bust. applyDataVersion() compares against it so
    // the persisted tab caches refresh when the backend data changes — making
    // the client-side cache reliable instead of purely time-based.
    dataVersion: null as number | null,

    // ─────────────────────────────────────────────
    // Persistent TTL markers
    // ─────────────────────────────────────────────
    householdsFetchedAt: null as number | null,
    registrationsFetchedAt: null as number | null,
    sleevesFetchedAt: null as number | null,
    representativesFetchedAt: null as number | null,

    // ─────────────────────────────────────────────
    // ✅ Persistent LISTS (so refresh doesn't refetch)
    // ─────────────────────────────────────────────
    households: [] as Household[],
    registrations: [] as Registration[],
    sleeves: [] as Sleeve[],
    representatives: [] as Representative[],
    sidebarRegistrationsByHousehold: {} as Record<string, SidebarCacheEnvelope<Registration[]>>,
    sidebarSleevesByHousehold: {} as Record<string, SidebarCacheEnvelope<SleevesPerReg[]>>,

    // ─────────────────────────────────────────────
    // ✅ Persistent SELECTED OBJECTS
    // (you explicitly asked for household; you said same for the others)
    // ─────────────────────────────────────────────
    household: null as Household | null,
    registration: null as Registration | null,
    sleeve: null as Sleeve | null,
    representative: null as Representative | null,

    // ─────────────────────────────────────────────
    // Per-account scoping (prevents leaking cached lists across accounts)
    // ─────────────────────────────────────────────
    accountNumber: null as string | null,

    // ─────────────────────────────────────────────
    // Runtime-only flags (not persisted)
    // ─────────────────────────────────────────────
    householdsLoading: false,
    registrationsLoading: false,
    sleevesLoading: false,
    representativesLoading: false,
    expandedRegistrationId: null as string | null
  }),

  getters: {
    // Basic selection flags
    hasHousehold: s => s.householdId !== null,
    hasRegistration: s => s.registrationId !== null,
    hasSleeve: s => s.sleeveId !== null,
    hasRepresentative: s => s.representativeId !== null,

    // TTL freshness
    householdsIsFresh: s =>
      s.householdsFetchedAt !== null
      && Date.now() - s.householdsFetchedAt < TTL.households,

    registrationsIsFresh: s =>
      s.registrationsFetchedAt !== null
      && Date.now() - s.registrationsFetchedAt < TTL.registrations,

    sleevesIsFresh: s =>
      s.sleevesFetchedAt !== null
      && Date.now() - s.sleevesFetchedAt < TTL.sleeves,

    representativesIsFresh: s =>
      s.representativesFetchedAt !== null
      && Date.now() - s.representativesFetchedAt < TTL.representatives
  },

  actions: {
    // ─────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────
    ensureClientOrAdmin(role?: Role | null) {
      // Adjust if you only want this behavior for client, etc.
      if (!role) return
      if (role !== 'client' && role !== 'admin') {
        // Not allowed to auto-fetch/cache for other roles
        throw new Error(`Role "${role}" not allowed to fetch tab session caches.`)
      }
    },

    clearRegistrationAndSleeveSelection() {
      this.registrationId = null
      this.sleeveId = null
      this.expandedRegistrationId = null

      this.registration = null
      this.sleeve = null
    },

    ensureAccount(accountNumber?: string | null) {
      if (!accountNumber) return

      if (this.accountNumber && this.accountNumber !== accountNumber) {
        // account changed -> clear account-scoped caches
        this.clearCaches()
      }

      this.accountNumber = accountNumber
    },

    // Keep selected objects consistent with IDs and refreshed lists
    syncSelectedFromLists() {
      if (this.householdId != null) {
        const hh = this.households.find(h => h.id === this.householdId) ?? null
        if (hh) this.household = hh
      }

      if (this.registrationId != null) {
        const reg = this.registrations.find(r => r.id === this.registrationId) ?? null
        if (reg) this.registration = reg
      }

      if (this.sleeveId != null) {
        const sl = this.sleeves.find(s => s.id === this.sleeveId) ?? null
        if (sl) this.sleeve = sl
      }

      if (this.representativeId != null) {
        const rep = this.representatives.find(r => r.id === this.representativeId) ?? null
        if (rep) this.representative = rep
      }
    },

    // ─────────────────────────────────────────────
    // ID + Object setters (selection)
    // ─────────────────────────────────────────────
    setHouseholdId(id: number | null) {
      const previousId = this.householdId
      this.householdId = id

      // If id changes, clear stale selected object if mismatch
      if (id === null || (this.household && this.household.id !== id)) {
        this.household = null
      }

      // Cascade-clear: when the household actually changes, every
      // piece of state scoped to the previous household is stale.
      // This fixes the bug where a Client whose admin reassigned them
      // to a new household kept seeing the previous household's name,
      // sidebar tree and selections because the persisted Pinia state
      // still held them.
      //
      // We deliberately keep the household-list cache around (it's
      // global, not per-household) and the representative selection
      // (rep is a property of the user, not the household; cleared
      // explicitly in clearSession when truly wiping context).
      const idChanged = previousId !== id && !(previousId == null && id == null)
      if (idChanged) {
        // Per-household selections + their backing lists.
        this.registrationId = null
        this.sleeveId = null
        this.expandedRegistrationId = null
        this.registration = null
        this.sleeve = null

        this.registrations = []
        this.registrationsFetchedAt = null
        this.sleeves = []
        this.sleevesFetchedAt = null

        // Sidebar payload caches are keyed by householdId — drop the
        // OLD household's entries. The new household's data will be
        // fetched on demand by the layout/page that needs it.
        if (previousId != null) {
          const oldKey = String(previousId)
          const { [oldKey]: _o,  ...restR } = this.sidebarRegistrationsByHousehold
          this.sidebarRegistrationsByHousehold = restR
          const { [oldKey]: _o2, ...restS } = this.sidebarSleevesByHousehold
          this.sidebarSleevesByHousehold = restS
        }
      }
    },

    setExpandedRegistrationId(id: string | null) {
      this.expandedRegistrationId = id
    },

    setRegistrationId(id: string | null) {
      this.registrationId = id
      if (id === null || (this.registration && this.registration.id !== id)) {
        this.registration = null
      }
    },

    setSleeveId(id: string | null) {
      this.sleeveId = id
      if (id === null || (this.sleeve && this.sleeve.id !== id)) {
        this.sleeve = null
      }
    },

    setRepresentativeId(id: number | null) {
      this.representativeId = id
      if (id === null || (this.representative && this.representative.id !== id)) {
        this.representative = null
      }
    },

    setHouseholdObj(hh: Household | null) {
      this.household = hh
      this.householdId = hh?.id ?? this.householdId
    },

    setRegistrationObj(reg: Registration | null) {
      this.registration = reg
      this.registrationId = reg?.id ?? this.registrationId
    },

    setSleeveObj(s: Sleeve | null) {
      this.sleeve = s
      this.sleeveId = s?.id ?? this.sleeveId
    },

    setRepresentativeObj(rep: Representative | null) {
      this.representative = rep
      this.representativeId = rep?.id ?? this.representativeId
    },

    // ─────────────────────────────────────────────
    // Fetchers with caching + TTL + SWR
    // ─────────────────────────────────────────────
    async fetchHouseholds(opts: FetchOptions = {}) {
      if (!import.meta.client) return this.households

      const { force = false, background = true, role = null, accountNumber = null } = opts
      this.ensureClientOrAdmin(role)
      this.ensureAccount(accountNumber)

      // Use fresh cache
      if (!force && this.households.length > 0 && this.householdsIsFresh) {
        return this.households
      }

      // Stale-while-revalidate
      if (!force && background && this.households.length > 0 && !this.householdsIsFresh) {
        this._refreshHouseholds(accountNumber).catch(() => {})
        return this.households
      }

      return await this._refreshHouseholds(accountNumber)
    },

    async _refreshHouseholds(accountNumber: string | null) {
      if (this.householdsLoading) return this.households
      this.householdsLoading = true
      try {
        // Adjust URL to your API
        const url = accountNumber
          ? `/api/households?accountNumber=${encodeURIComponent(accountNumber)}`
          : `/api/households`

        const households = await $fetch<Household[]>(url)
        this.households = households
        this.householdsFetchedAt = Date.now()

        this.syncSelectedFromLists()
        return households
      } finally {
        this.householdsLoading = false
      }
    },

    async fetchRegistrations(opts: FetchOptions & { householdId?: number | null } = {}) {
      if (!import.meta.client) return this.registrations

      const {
        force = false,
        background = true,
        role = null,
        accountNumber = null,
        householdId = this.householdId
      } = opts

      this.ensureClientOrAdmin(role)
      this.ensureAccount(accountNumber)

      if (!householdId) {
        // no household selected; nothing to fetch (or fetch all if that's your API)
        this.registrations = []
        this.registrationsFetchedAt = null
        return this.registrations
      }

      if (!force && this.registrations.length > 0 && this.registrationsIsFresh) {
        return this.registrations
      }

      if (!force && background && this.registrations.length > 0 && !this.registrationsIsFresh) {
        this._refreshRegistrations(householdId, accountNumber).catch(() => {})
        return this.registrations
      }

      return await this._refreshRegistrations(householdId, accountNumber)
    },

    async _refreshRegistrations(householdId: number, accountNumber: string | null) {
      if (this.registrationsLoading) return this.registrations
      this.registrationsLoading = true
      try {
        // Adjust URL to your API
        const url
          = `/api/registrations?householdId=${encodeURIComponent(String(householdId))}`
            + (accountNumber ? `&accountNumber=${encodeURIComponent(accountNumber)}` : '')

        const regs = await $fetch<Registration[]>(url)
        this.registrations = regs
        this.registrationsFetchedAt = Date.now()

        this.syncSelectedFromLists()
        return regs
      } finally {
        this.registrationsLoading = false
      }
    },

    async fetchSleeves(opts: FetchOptions & { registrationId?: string | null } = {}) {
      if (!import.meta.client) return this.sleeves

      const {
        force = false,
        background = true,
        role = null,
        accountNumber = null,
        registrationId = this.registrationId
      } = opts

      this.ensureClientOrAdmin(role)
      this.ensureAccount(accountNumber)

      if (!registrationId) {
        this.sleeves = []
        this.sleevesFetchedAt = null
        return this.sleeves
      }

      if (!force && this.sleeves.length > 0 && this.sleevesIsFresh) {
        return this.sleeves
      }

      if (!force && background && this.sleeves.length > 0 && !this.sleevesIsFresh) {
        this._refreshSleeves(registrationId, accountNumber).catch(() => {})
        return this.sleeves
      }

      return await this._refreshSleeves(registrationId, accountNumber)
    },

    async _refreshSleeves(registrationId: string, accountNumber: string | null) {
      if (this.sleevesLoading) return this.sleeves
      this.sleevesLoading = true
      try {
        // Adjust URL to your API
        const url
          = `/api/sleeves?registrationId=${encodeURIComponent(registrationId)}`
            + (accountNumber ? `&accountNumber=${encodeURIComponent(accountNumber)}` : '')

        const sleeves = await $fetch<Sleeve[]>(url)
        this.sleeves = sleeves
        this.sleevesFetchedAt = Date.now()

        this.syncSelectedFromLists()
        return sleeves
      } finally {
        this.sleevesLoading = false
      }
    },

    async fetchRepresentatives(opts: FetchOptions & { householdId?: number | null } = {}) {
      if (!import.meta.client) return this.representatives

      const {
        force = false,
        background = true,
        role = null,
        accountNumber = null,
        householdId = this.householdId
      } = opts

      this.ensureClientOrAdmin(role)
      this.ensureAccount(accountNumber)

      if (!householdId) {
        this.representatives = []
        this.representativesFetchedAt = null
        return this.representatives
      }

      if (!force && this.representatives.length > 0 && this.representativesIsFresh) {
        return this.representatives
      }

      if (!force && background && this.representatives.length > 0 && !this.representativesIsFresh) {
        this._refreshRepresentatives(householdId, accountNumber).catch(() => {})
        return this.representatives
      }

      return await this._refreshRepresentatives(householdId, accountNumber)
    },

    async _refreshRepresentatives(householdId: number, accountNumber: string | null) {
      if (this.representativesLoading) return this.representatives
      this.representativesLoading = true
      try {
        // Adjust URL to your API
        const url
          = `/api/representatives?householdId=${encodeURIComponent(String(householdId))}`
            + (accountNumber ? `&accountNumber=${encodeURIComponent(accountNumber)}` : '')

        const reps = await $fetch<Representative[]>(url)
        this.representatives = reps
        this.representativesFetchedAt = Date.now()

        this.syncSelectedFromLists()
        return reps
      } finally {
        this.representativesLoading = false
      }
    },

    // ─────────────────────────────────────────────
    // Clear helpers
    // ─────────────────────────────────────────────
    clearCaches() {
      this.households = []
      this.registrations = []
      this.sleeves = []
      this.representatives = []
      this.sidebarRegistrationsByHousehold = {}
      this.sidebarSleevesByHousehold = {}

      this.householdsFetchedAt = null
      this.registrationsFetchedAt = null
      this.sleevesFetchedAt = null
      this.representativesFetchedAt = null
    },

    clearSession() {
      this.householdId = null
      this.registrationId = null
      this.sleeveId = null
      this.representativeId = null
      this.expandedRegistrationId = null

      this.household = null
      this.registration = null
      this.sleeve = null
      this.representative = null

      this.accountNumber = null
      this.clearCaches()
    },

    // Reconcile against the backend data version (IQueryCache generation).
    // Returns true if the version CHANGED (so the caller can refetch the
    // household value). On a change we mark every time-TTL cache stale so the
    // next access refetches — the persisted lists stay visible (SWR) until the
    // refetch lands, so there's no blank flash. Null gen (e.g. last-import
    // failed) is a no-op so we degrade to the existing time-based TTLs.
    applyDataVersion(gen: number | null): boolean {
      if (gen == null) return false
      if (this.dataVersion === gen) return false
      this.dataVersion = gen
      this.householdsFetchedAt = null
      this.registrationsFetchedAt = null
      this.sleevesFetchedAt = null
      this.representativesFetchedAt = null
      this.sidebarRegistrationsByHousehold = {}
      this.sidebarSleevesByHousehold = {}
      return true
    },

    setSidebarRegistrationsCache(householdId: number, data: Registration[]) {
      // Sidebar cache payloads are stored in the same persisted tab session as
      // the rest of the left-nav state, so refreshes within this tab reuse the
      // last successful API result without a second storage mechanism.
      this.sidebarRegistrationsByHousehold[String(householdId)] = buildSidebarCacheEnvelope(data)
    },

    getSidebarRegistrationsCacheEntry(householdId: number) {
      return this.sidebarRegistrationsByHousehold[String(householdId)] ?? null
    },

    removeSidebarRegistrationsCache(householdId: number) {
      const cacheKey = String(householdId)
      const { [cacheKey]: _removed, ...rest } = this.sidebarRegistrationsByHousehold
      this.sidebarRegistrationsByHousehold = rest
    },

    setSidebarSleevesCache(householdId: number, data: SleevesPerReg[]) {
      // Grouped sleeve rows are cached beside registration rows so both
      // registration and sleeve pages can hydrate the same sidebar instantly.
      this.sidebarSleevesByHousehold[String(householdId)] = buildSidebarCacheEnvelope(data)
    },

    getSidebarSleevesCacheEntry(householdId: number) {
      return this.sidebarSleevesByHousehold[String(householdId)] ?? null
    },

    removeSidebarSleevesCache(householdId: number) {
      const cacheKey = String(householdId)
      const { [cacheKey]: _removed, ...rest } = this.sidebarSleevesByHousehold
      this.sidebarSleevesByHousehold = rest
    }
  },

  // Tab-scoped persisted state (sessionStorage)
  persist: {
    storage: piniaPluginPersistedstate.sessionStorage(),
    pick: [
      // IDs
      'householdId',
      'registrationId',
      'sleeveId',
      'representativeId',

      // ✅ Persist lists + TTL markers (prevents refetch on refresh)
      'households',
      'householdsFetchedAt',
      'registrations',
      'registrationsFetchedAt',
      'sleeves',
      'sleevesFetchedAt',
      'representatives',
      'representativesFetchedAt',
      'sidebarRegistrationsByHousehold',
      'sidebarSleevesByHousehold',

      // ✅ Persist selected objects (YOU ASKED FOR THIS)
      'household',
      'registration',
      'sleeve',
      'representative',

      // Account scoping
      'accountNumber',

      // Backend data-version stamp for the cached household data.
      'dataVersion',

      'expandedRegistrationId'
    ]
  }
})
